import CONSTANTS from '../../../constants.js';
import urls from '../../urls.js';
const validate = require('validate.js');
const httpStatus = require('http-status-codes');
const moment = require('moment');
import Field from './_field.js';

export default {
    namespaced: true,
    state() {
        return {
            fields: [],
            recordId: undefined,
            currentField: {} /* may be used as currently selected or focused field for future purpose,
                                currently selected for displaying in a modal custom field for now */
        }
    },
    mutations: {
        setFields(state, fields) {
            state.fields = fields;
        },
        pushField(state, field) {
            state.fields.push(field);
        },
        removeField(state, fieldKey) {
            state.fields = state.fields.filter(f => f.key !== fieldKey);
        },
        setFieldAttr(state, [key, attr, value]) {
            const field = state.fields.find(f => f.key === key);
            field[attr] = value;
        },
        setFieldsAttrs(state, [attr, data]) {
            // data is an object in format: {<field#1 key>: <field#1 value>, <field#2 key>: <field#2 value> ...}
            state.fields.forEach(field => {
                if (field.key in data) {
                    field[attr] = data[field.key];
                }
            });
        },
        setFieldPostpendAttr(state, [fieldKey, attr, value]) {
            const field = state.fields.find(f => f.key === fieldKey);
            field.postpend[attr] = value;
        },
        setRecordId(state, recordId) {
            state.recordId = recordId;
        },
        clearValues(state) {
            state.fields.forEach(f => {
                f.value = '';
                f.validationValue = '';
            });
        },
        setCurrentField(state, field) {
            state.currentField = field;
        }
    },
    getters: {
        errorMessage(state, getters) {
            // Do not show any form-related error message if enrichment status has been provided.
            const enrichmentStatusField = state.fields.find(f => f.key === CONSTANTS.FIELDS.ENRICHMENT_STATUS.key);
            if (enrichmentStatusField && enrichmentStatusField.value) {
                return;
            }
            if (state.fields.some(f => !!f.error)) {
                return 'Please fix all form errors first.';
            }
            if (state.fields.some(f => f.required && !f.value)) {
                return 'Please fill all required fields.'
            }
            // All empty values (null, undefined, "") are considered same
            if (state.fields.every(f => f.value === f.originValue || !f.value && !f.originValue)) {
                // If there are any trusted fields no fields need to be changed
                if (!state.fields.some(f => f.trusted)) {
                    return 'At least one field should be changed.';
                }
            }
        },
        isUpdate(state) {
            return !!state.recordId;
        },
    },
    actions: {
        initFields,
        initRecord,
        initPriorityFields,
        toggleFieldPostpend,
        validateField,
        validateVerificationField,
        processValue,
        showErrors,
    },
};

function initFields(context, backendFields) {
    const fields = backendFields.map(f => _backendFieldToField(f));
    context.commit('setFields', fields);

    // set multipicklist fields
    const picklistMultiFields = fields.filter(f => f.validation === 'multipicklist');

    if (picklistMultiFields.length) {
        picklistMultiFields.map(picklistField => {
            context.commit('setFieldAttr', [picklistField.key, 'type', 'multi-picklist']);
        })
    }
}

function _backendFieldToField(backendField) {
    const fieldAttrs = {
        id: backendField.id,
        name: backendField.name,
        key: backendField.key,
        required: backendField.required,
        validation: backendField.validation,
        verification: backendField.verification,
        custom: backendField.custom,
        picklistType: backendField.picklist_type,
        description: backendField.description,
    };
    if (backendField.update_rule) {
        const updateRule = Object.values(CONSTANTS.ENRICHMENT.MAPPING_TYPE).find(
            rule => rule.key === backendField.update_rule);
        fieldAttrs.updateRule = updateRule.caption;
    }
    if (backendField.choices && backendField.choices.length) {
        fieldAttrs.type = 'select';
        fieldAttrs.choices = backendField.choices;
    } else {
        fieldAttrs.type = 'text';
    }
    return new Field(fieldAttrs);
}

/* Update existing form fields based on record that is currently edited. */
function initRecord(context, backendRecord) {
    _clearRecord(context);
    context.commit('setRecordId', backendRecord.id);
    // Initialize form fields values and verification values
    const fieldsValues = Object.assign({}, backendRecord.data);
    context.commit('setFieldsAttrs', ['value', fieldsValues]);
    context.commit('setFieldsAttrs', ['originValue', fieldsValues]);
    const fieldsVerificationValues = Object.assign({}, backendRecord.verification_data || {});
    context.commit('setFieldsAttrs', ['verificationValue', fieldsVerificationValues]);
    context.commit('setFieldsAttrs', ['trusted', _getTrustedFields(backendRecord)]);
    // Initialize status field
    context.commit('pushField', _getStatusField(backendRecord));
    // Initialize enrichment status field
    const enrichmentStatusField = _getEnrichmentStatusField(backendRecord);
    if (enrichmentStatusField) {
        context.commit('pushField', enrichmentStatusField);
    }
    // Initialize fields postpends
    const postpends = _getFieldsPostpends(backendRecord);
    context.commit('setFieldsAttrs', ['postpend', postpends]);
    // Mark fields as not editable based on meta data
    if (!backendRecord.meta_data.editable) {
        context.state.fields.forEach(field => {
            context.commit('setFieldAttr', [field.key, 'editable', false]);
        });
    }
}

/* Initialize fields that should be entered by user before all other ones. Disable all other fields. */
function initPriorityFields(context, keys) {
    const priorityKeys = {};
    context.state.fields.forEach(f => {
        priorityKeys[f.key] = keys.includes(f.key);
    });
    context.commit('setFieldsAttrs', ['prioritized', priorityKeys]);
}

function _clearRecord(context) {
    context.commit('setRecordId', undefined);
    context.commit('removeField', CONSTANTS.FIELDS.STATUS.key);
    context.commit('removeField', CONSTANTS.FIELDS.ENRICHMENT_STATUS.key);

    const fields = context.state.fields;
    fields.forEach(field => {
        context.commit('setFieldAttr', [field.key, 'value', '']);
        context.commit('setFieldAttr', [field.key, 'originValue', '']);
        context.commit('setFieldAttr', [field.key, 'verificationValue', '']);
        context.commit('setFieldAttr', [field.key, 'editable', true]);
        context.commit('setFieldAttr', [field.key, 'postpend', {disabled: true}]);
    })
}

function _backendStatusFieldToField(backendStatusField, currentStatus) {
    const value = CONSTANTS.STATUS_CHOICES.find(field => field.value === currentStatus).value;
    return new Field({
        type: 'select',
        name: backendStatusField.name,
        key: backendStatusField.key,
        required: true,
        value: value,
        originValue: value,
        choices: backendStatusField.choices,
        editable: backendStatusField.choices.length > 1,
        isDataField: false,
    })
}

function _getStatusField(backendRecord) {
    const backendStatusField = backendRecord.meta_data.status_fields.find(
        field => field.key === CONSTANTS.FIELDS.STATUS.key);
    return _backendStatusFieldToField(backendStatusField, backendRecord.status);
}

function _backendEnrichmentStatusFieldToField(backendEnrichmentStatusField, currentEnrichmentStatusId) {
    return new Field({
        type: 'select',
        name: backendEnrichmentStatusField.name,
        key: backendEnrichmentStatusField.key,
        required: !!currentEnrichmentStatusId,
        value: currentEnrichmentStatusId,
        originValue: currentEnrichmentStatusId,
        choices: backendEnrichmentStatusField.choices,
        editable: backendEnrichmentStatusField.choices.length > 1,
        isDataField: false,
    })
}

function _getEnrichmentStatusField(backendRecord) {
    const backendEnrichmentStatusField = backendRecord.meta_data.status_fields.find(
        field => field.key === CONSTANTS.FIELDS.ENRICHMENT_STATUS.key);
    if (!backendEnrichmentStatusField) {
        return null;
    }
    const currentEnrichmentStatusId = backendRecord.enrichment_status ? backendRecord.enrichment_status.id : null;
    return _backendEnrichmentStatusFieldToField(backendEnrichmentStatusField, currentEnrichmentStatusId);
}

/**
 * Generates postpends based on record meta data.
 * Result is formatted as {<field key>: <postpend> ...}
 */
function _getFieldsPostpends(backendRecord) {
    const fieldsMetaData = backendRecord.meta_data.canonical_fields;
    if (!fieldsMetaData) {
        return {};
    }

    function getPostpend(fieldMetaData) {
        if (fieldMetaData.trusted) {
            return {
                type: 'bizgraph-mark',
                tooltip: 'Previously Finalized',
            }
        } else if (fieldMetaData.auto_enriched) {
            return {
                type: 'warning',
            }
        }
        return {disabled: true};
    }

    const postpends = {};
    Object.entries(fieldsMetaData).forEach(([fieldKey, fieldMetaData]) => {
        postpends[fieldKey] = getPostpend(fieldMetaData);
    });
    return postpends;
}

function _getTrustedFields(backendRecord) {
    const trustedFields = {};
    const fieldsMetaData = backendRecord.meta_data.canonical_fields;
    if (fieldsMetaData) {
        Object.entries(fieldsMetaData).filter(
            ([fieldKey, fieldMetaData]) => fieldMetaData.trusted
        ).forEach(
            ([fieldKey, fieldMetaData]) => trustedFields[fieldKey] = true
        )
    }
    return trustedFields;
}

/* Disable bizgraph mark or warning if original value was changed. */
function toggleFieldPostpend(context, [key, value]) {
    const field = context.state.fields.find(f => f.key === key);
    const toggableTypes = ['bizgraph-mark', 'warning'];
    if (!toggableTypes.includes(field.postpend.type)) {
        return;
    }
    const postpendEnabled = value === field.originValue;
    context.commit('setFieldPostpendAttr', [field.key, 'disabled', !postpendEnabled]);
}

// This map corresponds to backend fields validation.
// It uses validate.js format to describe fields restriction.

validate.extend(validate.validators.datetime, {
  // The value is guaranteed not to be null or undefined but otherwise it
  // could be anything.
  parse: function(value, options) {
    return +moment.utc(value);
  },
  // Input is a unix timestamp
  format: function(value, options) {
    var format = options.dateOnly ? "YYYY-MM-DD" : "YYYY-MM-DDThh:mm:ssZ";
    return moment.utc(value).format(format);
  },
});

const _validationMap = {
    integer: {
        numericality: {
            onlyInteger: true,
        },
    },
    float: {
        numericality: true,
    },
    decimal: {
        format: {
            pattern: /^\d+.\d{2}$/,
            message: 'should be float with two digits after decimal point',
        },
    },
    string: {
        length: {
            maximum: 255,
        },
    },
    text: {
        length: {
            maximum: 255,
        },
    },
    address: {
        length: {
            maximum: 255,
        },
    },
    text: {
        length: {
            maximum: 1000,
        },
    },
    textarea: {
        length: {
            maximum: 1000,
        },
    },
    address: {
        length: {
            maximum: 255,
        },
    },
    email: {
        email: true,
    },
    phone: {
        length: {
            maximum: 25,
        },
        format: /^[0-9\-\+\(\)\s]+$/,
    },
    url: {
        url: true,
    },
    zipcode: {
        length: {
            maximum: 10,
            format: /\d{5}(-\d{4})?/,
        },
    },
    linkedin_url: {
        format: {
            pattern: /^.*linkedin.com.*$/,
            message: 'should contain linkedin.com',
        },
    },
    boolean: {
        inclusion: {
            within: ['true', 'false'],
            message: "allowed values: true, false"
        }
    },
    datetime: {
        datetime: {
            message: "must be a valid date. allowed format: YYYY-MM-DDThh:mm:ssZ"
        }
    },
    date: {
        datetime: {
            dateOnly: true,
            message: "must be a valid date. allowed format: YYYY-MM-DD"
        }
    },
//    time: {
//        format: {
//            pattern: /^\d{2}\:\d{2}:\d{2}.\d{3}Z$/,
//            message: "must be a valid date. allowed format: HH:mm:ss.SSSZ"
//        }
//    },
    percent: {
        numericality: true,
    },
    currency: {
        numericality: true,
    },
};

function _asLink(value) {
    if (value.split('http').length > 2 || value.length === 254) {
        return 'not url'
    }

    if (value && value.indexOf('//') === -1) {
        return 'http://' + value.trim();
    }
    return value.trim();
}

function validateField(context, [key, value]) {
    const field = context.state.fields.find(f => f.key === key);
    // Do not apply validation for origin value.
    if (value == field.originValue) {  // "==" because we do not care if it is empty, null or undefined
        context.commit('setFieldAttr', [key, 'error', null]);
        return;
    }
    // If value is not defined - validate based on field "required" attribute.
    if (!value) {
        const error = field.required ? 'required' : null;
        context.commit('setFieldAttr', [key, 'error', error]);
        return;
    } else {
        context.commit('setFieldAttr', [key, 'error', null]);
    }
    // Use validate.js constraints to validate field value
    const constraints = _validationMap[field.validation];
    if (!constraints) {
        return;
    }
    const toValidate = field.validation === 'url' ? _asLink(value) : value;
    const errors = validate.single(toValidate, constraints);
    const error = errors && errors.length ? errors[0] : null;
    context.commit('setFieldAttr', [key, 'error', error]);
}

function validateVerificationField(context, [key, value]) {
    const constraints = {url: true};
    const errors = validate.single(_asLink(value), constraints);
    const error = errors && errors.length ? errors[0] : null;
    context.commit('setFieldAttr', [key, 'verificationError', error]);
}

function _processLinkedInField(context, key, value) {
    const field = context.state.fields.find(field => field.key === key && field.validation === 'linkedin_url');
    // strip params off of linkedin addresses
    if (field) {
        return value.split('?')[0];
    }
    return value;
}

/* Unlock all disabled fields since user already has entered some value to priority field */
function _unlockNonPriorityFields(context, key, value) {
    if (context.state.fields.every(f => f.prioritized)) {
        return;
    }
    const field = context.state.fields.find(f => f.key === key && f.prioritized);
    if (!field || value.length < CONSTANTS.MIN_BIZGRAPH_UNLOCK_LENGTH) {
        return;
    }
    context.state.fields.forEach(f => {
        context.commit('setFieldAttr', [f.key, 'prioritized', true]);
    });
}

// State field has choices only for US. We need to display choices only if country is US.
function _toggleStateFieldTypeOnCountryChange(context, key, value) {
    if (key !== CONSTANTS.FIELDS.COUNTRY.key) {
        return;
    }
    const stateField = context.state.fields.find(f => f.key === CONSTANTS.FIELDS.STATE.key);
    if (!stateField) {
        return;
    }
    const USANames = ['united states', 'us', 'usa'];
    if (USANames.includes((value || '').toLowerCase())) {
        stateField.type = 'select';
    } else {
        stateField.type = 'text';
    }
}

function processValue(context, [key, value]) {
    value = _processLinkedInField(context, key, value);
    _unlockNonPriorityFields(context, key, value);
    _toggleStateFieldTypeOnCountryChange(context, key, value);
    return value;
}

function _addErrorsToFields(context, errors) {
    Object.entries(errors).forEach(([key, error]) => {
        const message = Array.isArray(error) ? error[0] : error;
        context.commit('setFieldAttr', [key, 'error', message]);  // backend provides errors as a list.
    });
    notie.alert('error', 'Please fix form fields errors.', CONSTANTS.ALERT_TIMES.MEDIUM);
}

/* Show backend errors in form. Connect them to fields if possible. */
function showErrors(context, response) {
    // Processes and displays error messages after `create` or `update` operation
    if (!response.status || response.status >= httpStatus.INTERNAL_SERVER_ERROR) {
        notie.alert('error', 'Fatal error on server. Please contact support.', CONSTANTS.ALERT_TIMES.MEDIUM);
        return;
    } else if (response.status === httpStatus.BAD_REQUEST) {
        // Handle field level errors
        if (response.body.data) {
            _addErrorsToFields(context, response.body.data);
            return;
        }
        // Handle validation data errors
        if (response.body.verification_data) {
            _addErrorsToFields(context, response.body.verification_data);
            return;
        }

        // Handle all other errors
        if (Object.keys(response.body).length) {
            const error = Object.values(response.body)[0];
            notie.alert('error', error, CONSTANTS.ALERT_TIMES.MEDIUM);
            return;
        }
    } else {
        throw Error('Method "record-form-store.showErrors" was called for unexpected response status.');
        return;
    }
    notie.alert(
        'error',
        'Unexpected exception happened, please contact developers.',
        CONSTANTS.ALERT_TIMES.MEDIUM
    );
    throw Error('Method "record-form-store.showErrors" was not able to handle error properly.');
}
